import { Graphics, type FillInput } from 'pixi.js';
import type { IsoProjection } from '../core/projection';
import { createShoreGeometry, shoreVariation, type ShoreSample } from './shoreline';

/** Static, batched bank geometry; animation and authored water masks stay in CityMap. */
export function drawShorelines(graphics: Graphics, cells: ReadonlyArray<readonly [number, number]>, size: number, projection: IsoProjection, groundFill?: FillInput): void {
  graphics.label = 'water-shorelines';
  const { contours, landPatches } = createShoreGeometry(cells, size);
  const scale = projection.tileHeight / 32;
  for (const patch of landPatches) {
    graphics.poly(patch.flatMap(point => {
      const screen = projection.worldToScreen(point.x, point.y);
      return [screen.x, screen.y];
    }));
  }
  graphics.fill(groundFill ?? { color: 0x2f4147 });
  const position = (point: ShoreSample, distance: number, elevation = 0) => {
    const variation = shoreVariation(point.x, point.y);
    const offset = (distance < 0 ? distance : distance * point.depth * (1 + variation * .22))
      + variation * .055 * Math.min(1, point.depth / .2);
    const screen = projection.worldToScreen(point.x + point.nx * offset, point.y + point.ny * offset);
    return { x: screen.x, y: screen.y - elevation * scale };
  };
  // Water-facing shallows, wet foot, ochre soil face, and a thin mossy turf cap.
  // Broad desaturated earth faces give the bank depth instead of a gold outline.
  const bands = [
    { outer: -.075, inner: 1.45, top: 0, bottom: 0, color: 0x70b6b5, alpha: .25 },
    { outer: -.065, inner: 1.03, top: 0, bottom: 0, color: 0x294e4a, alpha: .55 },
    { outer: -.065, inner: .87, top: 3.6, bottom: -.6, color: 0x594e43, alpha: 1 },
    { outer: -.07, inner: .46, top: 5.1, bottom: 1.1, color: 0x948269, alpha: 1 },
    { outer: -.13, inner: .13, top: 5.6, bottom: 4.8, color: 0x52744d, alpha: 1 },
  ];
  for (const band of bands) {
    for (const contour of contours) for (let i = 0; i < contour.length; i++) {
      const a = contour[i], b = contour[(i + 1) % contour.length];
      const p = position(a, band.outer, band.top), q = position(b, band.outer, band.top);
      const r = position(b, band.inner, band.bottom), s = position(a, band.inner, band.bottom);
      graphics.poly([p.x, p.y, q.x, q.y, r.x, r.y, s.x, s.y]);
    }
    graphics.fill({ color: band.color, alpha: band.alpha });
  }
  // Light catches exposed soil in broken horizontal strata, never every cell.
  for (const contour of contours) for (const point of contour) {
    if (shoreVariation(point.x * 1.8, point.y * 1.8) < .48) continue;
    const a = position(point, .38, 2.3), b = position(point, .65, 1.2);
    graphics.moveTo(a.x - 1.2 * scale, a.y).lineTo(b.x + 1.6 * scale, b.y);
  }
  graphics.stroke({ color: 0xb1a18a, alpha: .45, width: .7 * scale });

  // World-anchored sparse rocks and vegetation. A single Graphics object is
  // rebuilt only with the map, so mobile rendering needs no per-cell sprites.
  const decorations: Array<{ point: ShoreSample; hash: number }> = [];
  let lastX = Infinity, lastY = Infinity;
  for (const contour of contours) for (const point of contour) {
    if (Math.hypot(point.x - lastX, point.y - lastY) < .65) continue;
    const hash = (Math.imul(Math.round(point.x * 29), 73856093) ^ Math.imul(Math.round(point.y * 29), 19349663)) >>> 0;
    if (hash % 11 > 2) continue;
    decorations.push({ point, hash });
    lastX = point.x; lastY = point.y;
  }
  for (const { point, hash } of decorations) {
    if (hash % 3 !== 0) continue;
    const p = position(point, .9, -.3), radius = (2.3 + (hash % 5) * .3) * scale;
    graphics.ellipse(p.x + .6 * scale, p.y + .65 * scale, radius * 1.2, radius * .5);
  }
  graphics.fill({ color: 0x233e3b, alpha: .45 });
  for (const { point, hash } of decorations) {
    if (hash % 3 !== 0) continue;
    const p = position(point, .79, .8), r = (2.3 + (hash % 5) * .3) * scale;
    graphics.poly([p.x - r, p.y, p.x - r * .45, p.y - r * .85, p.x + r * .4, p.y - r * .7, p.x + r, p.y, p.x, p.y + r * .4]);
    graphics.ellipse(p.x + r * 1.4, p.y + r * .28, r * .5, r * .3);
  }
  graphics.fill({ color: 0x7c877e, alpha: 1 });
  for (const { point, hash } of decorations) {
    if (hash % 3 !== 0) continue;
    const p = position(point, .79, .8), r = (2.3 + (hash % 5) * .3) * scale;
    graphics.poly([p.x - r, p.y, p.x - r * .45, p.y - r * .85, p.x + r * .4, p.y - r * .7, p.x + r * .15, p.y - r * .12]);
  }
  graphics.fill({ color: 0xb0b5a4, alpha: .95 });
  for (const { point, hash } of decorations) {
    if (hash % 3 === 0) continue;
    const p = position(point, -.12, 5.1);
    graphics.ellipse(p.x, p.y, 2.6 * scale, 1.1 * scale);
  }
  graphics.fill({ color: 0x3f6245, alpha: 1 });
  for (const { point, hash } of decorations) {
    if (hash % 3 === 0) continue;
    const p = position(point, -.12, 5.1), height = (4.5 + hash % 4) * scale;
    graphics.moveTo(p.x, p.y).quadraticCurveTo(p.x - 1.7 * scale, p.y - height * .6, p.x - 2 * scale, p.y - height);
    graphics.moveTo(p.x, p.y).lineTo(p.x + .3 * scale, p.y - height * 1.15);
    graphics.moveTo(p.x, p.y).quadraticCurveTo(p.x + 1.8 * scale, p.y - height * .5, p.x + 2.6 * scale, p.y - height * .8);
  }
  graphics.stroke({ color: 0x77975e, alpha: .95, width: 1.2 * scale });
  for (const { point, hash } of decorations) {
    if (hash % 3 === 0) continue;
    const p = position(point, -.12, 5.1), height = (4.5 + hash % 4) * scale;
    graphics.moveTo(p.x + .5 * scale, p.y - scale).lineTo(p.x + .3 * scale, p.y - height * 1.15);
  }
  graphics.stroke({ color: 0xa5b57d, alpha: .85, width: .65 * scale });
}
